<template>
  <div class="wrapper">
    <div class="header_wrapper">
      <v-toolbar color="primary" dark>
        <div>
          <v-btn @click="routerBack" icon>
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
        </div>
        <v-toolbar-title
          style="
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            margin: 0;
            padding: 0;
            max-width: 60%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          "
          class="text-center"
        >
          {{ childName }}
        </v-toolbar-title>
      </v-toolbar>
    </div>

    <div v-if="fetchLoading" class="d-flex justify-center align-center" style="height: 80vh;">
      <vue-loading
        :active="fetchLoading"
        loader="bars"
        color="#EE6090"
        :size="{ width: '30px', height: '30px' }"
      ></vue-loading>
    </div>

    <div v-else class="contents-wrapper">
      <v-tabs v-model="tabs" fixed-tabs color="primary">
        <v-tab value="memory">{{ t('growth.memory') }}</v-tab>
        <v-tab value="graph">{{ t('growth.graph') }}</v-tab>
      </v-tabs>

      <div class="pa-2 mt-1">
        <v-select
          v-model="selectedNendo"
          :items="nendoOptions"
          :label="t('growth.nendo')"
          variant="outlined"
          hide-details
          density="compact"
          @update:model-value="changeNendo"
        ></v-select>
      </div>

      <v-window v-model="tabs" class="pb-16">
        <v-window-item value="memory">
          <v-table v-if="growths && growths.length > 0" class="mt-2">
            <thead>
              <tr>
                <th class="text-left">{{ t('growth.implementationMonth') }}</th>
                <th class="text-left">{{ t('growth.height') }}</th>
                <th class="text-left">{{ t('growth.weight') }}</th>
                <th class="text-left"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="g in growths" :key="`${g.year}-${g.month}`">
                <td>{{ formatMonthName(g.month) }}</td>
                <td>{{ g.height }}cm</td>
                <td>{{ g.weight }}kg</td>
                <td>
                  <v-btn @click="toggleDetail(g)" color="primary" size="small">{{ t('growth.show') }}</v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>
          <div v-else class="pa-4 text-center">
            <p>{{ t('growth.noData') }}</p>
          </div>
        </v-window-item>

        <v-window-item value="graph">
          <div v-if="growths && growths.length > 0" class="pa-2">
            <GChart type="ComboChart" :data="heightChartData" :options="chartOptions(t('growth.height'))" />
            <div class="legend-container mb-8">
              <div class="d-flex align-center">
                <div class="skyblue-legend"></div>
                <span class="text-caption ml-1">{{ t('growth.percentileRange') }}</span>
              </div>
              <div class="d-flex align-center mt-1">
                <div class="legend-line-group">
                  <div class="legend-line"></div>
                  <div class="legend-dot"></div>
                </div>
                <span class="text-caption ml-1">{{ t('growth.height') }}</span>
              </div>
            </div>

            <GChart type="ComboChart" :data="weightChartData" :options="chartOptions(t('growth.weight'))" />
            <div class="legend-container mb-10">
              <div class="d-flex align-center">
                <div class="skyblue-legend"></div>
                <span class="text-caption ml-1">{{ t('growth.percentileRange') }}</span>
              </div>
              <div class="d-flex align-center mt-1">
                <div class="legend-line-group">
                  <div class="legend-line"></div>
                  <div class="legend-dot"></div>
                </div>
                <span class="text-caption ml-1">{{ t('growth.weight') }}</span>
              </div>
            </div>
          </div>
          <div v-else class="pa-4 text-center">
            <p>{{ t('growth.noData') }}</p>
          </div>
        </v-window-item>
      </v-window>
    </div>

    <v-dialog v-model="showDetail" max-width="500">
      <v-card v-if="selectedGrowth">
        <v-card-title class="text-h6 text-center">
          {{ formatMonthName(selectedGrowth.month) }} {{ selectedGrowth.child.lastName }} {{ selectedGrowth.child.firstName }}
        </v-card-title>
        <v-card-text>
          <div class="text-caption text-grey-darken-1">{{ t('growth.height') }}</div>
          <div class="text-body-1 mb-3">{{ selectedGrowth.height }}cm</div>
          <div class="text-caption text-grey-darken-1">{{ t('growth.weight') }}</div>
          <div class="text-body-1 mb-3">{{ selectedGrowth.weight }}kg</div>
          <div class="text-caption text-grey-darken-1">{{ t('growth.remarks') }}</div>
          <div class="text-body-1" style="white-space: pre-wrap">{{ selectedGrowth.remarks }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="showDetail = false">{{ t('growth.close') }}</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import 'dayjs/locale/en';
import 'dayjs/locale/km';
import { GChart } from 'vue-google-charts';
import { growthModule } from '@/store/entityModules/growth';
import { familyModule } from '@/store/entityModules/family';
import { GrowthResponse } from '@API/src/component/home/growth/types';
import { t, locale, dayjsLocale as appDayjsLocale } from '@/i18n';

import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';

dayjs.locale(appDayjsLocale);
const CHART_START_MONTH = 8;

export default defineComponent({
  name: 'GrowthDetail',

  components: {
    'vue-loading': Loading,
    GChart,
  },

  data: () => ({
    tabs: 'memory',
    selectedNendo: '',
    childId: NaN,
    childName: '',
    fetchLoading: false,
    showDetail: false,
    selectedGrowth: null as GrowthResponse | null,
    colorPrimary: '#029ec0',
    colorChartArea: '#67c4d9',
  }),

  computed: {
    family() {
      return familyModule.family;
    },
    growths() {
      return growthModule.growths || [];
    },
    selectedChild() {
      return this.family?.children.find(c => c.childId === this.childId) || null;
    },
    nendoOptions() {
      const nendoArray: string[] = [];
      const now = dayjs();
      let offset = (now.month() + 1) < CHART_START_MONTH ? -1 : 0;
      for (let i = 0; i < 6; i++) {
        nendoArray.push(now.add(offset - i, 'year').format('YYYY'));
      }
      return nendoArray;
    },
    heightChartData() {
      const data: any[] = [[t('growth.implementationMonth'), 'P3', 'P97-P3', t('growth.height')]];
      [...Array(12)].map((_, i) => (i + CHART_START_MONTH) % 12 || 12).forEach(m => {
        const year = m < CHART_START_MONTH ? Number(this.selectedNendo) + 1 : Number(this.selectedNendo);
        const { p3, p97 } = this.getPercentileValues('height', year, m);
        const found = this.growths.find(r => r.month === m);
        data.push([this.formatMonth(m), p3, (p3 && p97) ? p97 - p3 : null, found ? found.height : null]);
      });
      return data;
    },
    weightChartData() {
      const data: any[] = [[t('growth.implementationMonth'), 'P3', 'P97-P3', t('growth.weight')]];
      [...Array(12)].map((_, i) => (i + CHART_START_MONTH) % 12 || 12).forEach(m => {
        const year = m < CHART_START_MONTH ? Number(this.selectedNendo) + 1 : Number(this.selectedNendo);
        const { p3, p97 } = this.getPercentileValues('weight', year, m);
        const found = this.growths.find(r => r.month === m);
        data.push([this.formatMonth(m), p3, (p3 && p97) ? p97 - p3 : null, found ? found.weight : null]);
      });
      return data;
    },
  },

  methods: {
    t,
    routerBack() {
      this.$router.replace('/growth/select');
    },
    async changeNendo() {
      this.fetchLoading = true;
      await growthModule.fetchGrowth({ childId: String(this.childId), year: this.selectedNendo });
      this.fetchLoading = false;
    },
    toggleDetail(g: any) {
      if (g) {
        this.selectedGrowth = g;
        this.showDetail = true;
      } else {
        this.showDetail = false;
      }
    },
    formatMonth(month: number) {
      return t('record.date.month', 'month', { month });
    },
    formatMonthName(month: number) {
      return dayjs().locale(locale).month(month - 1).format('MMMM');
    },
    getPercentileValues(type: 'height' | 'weight', year: number, month: number) {
      if (this.selectedChild && growthModule.percentile) {
        const diffMonths = dayjs(`${year}-${month}`).diff(dayjs(this.selectedChild.dateOfBirth), 'month');
        const values = growthModule.percentile
          .filter(pm => pm.sex === this.selectedChild?.gender && pm.month === diffMonths && pm.valueType === type)
          .sort((a, b) => a.percentile - b.percentile);
        if (values.length === 7) return { p3: values[0].value, p97: values[6].value };
      }
      return { p3: null, p97: null };
    },
    chartOptions(title: string) {
      return {
        title,
        colors: ['transparent', this.colorChartArea, this.colorPrimary],
        seriesType: 'area',
        isStacked: true,
        series: {
          0: { lineWidth: 0, enableInteractivity: false, visibleInLegend: false },
          1: { lineWidth: 0, enableInteractivity: false },
          2: { lineWidth: 2, pointSize: 6, type: 'line' },
        },
        chartArea: { width: '85%', height: '70%' },
        legend: { position: 'none' },
      };
    },
  },

  async mounted() {
    this.selectedNendo = this.nendoOptions[0];
    this.childName = String(this.$route.query.childName || '');

    if (this.$route.query.childId && familyModule.family) {
      this.childId = Number(this.$route.query.childId);
      this.fetchLoading = true;
      try {
        await Promise.all([
          growthModule.fetchGrowth({ childId: String(this.childId), year: this.selectedNendo }),
          growthModule.fetchGrowthPercentile({ familyId: familyModule.family.familyId }),
        ]);
      } finally {
        this.fetchLoading = false;
      }
    } else {
      this.routerBack();
    }
  },
});
</script>

<style lang="scss" scoped>
.wrapper {
  padding-top: calc(var(--app-top-offset) + 56px); // ← ヘッダー分
  margin-bottom: calc(env(safe-area-inset-bottom) + 56px);
}

/* ===== ヘッダー ===== */
.header_wrapper {
  height: 56px;
  width: 100%;
  position: fixed;
  top: var(--app-top-offset); // ← 超重要
  left: 0;
  z-index: 100;

  .outline {
    border: 1px solid rgb(var(--v-theme-primary));
  }
}

/* ===== フォーム余白 ===== */
.v-form {
  margin-top: 8px;
}

/* ===== ラベルサイズ ===== */
.label-size > .v-input__control > .v-input__slot > .v-input--selection-controls__input > label {
  font-size: 12px;
}
// リストはヘッダー分だけ下げる
.v-list {
  margin-top: calc(env(safe-area-inset-top));
}
.wrapper {
  .legend-container {
    padding-left: 20px;
    margin-top: 10px;
  }

  .skyblue-legend { width: 15px; height: 10px; background-color: #67c4d9; border-radius: 2px; }
  
  .legend-line-group {
    position: relative;
    width: 15px;
    height: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .legend-line {
    width: 100%;
    height: 2px;
    background-color: #029ec0;
  }

  .legend-dot {
    position: absolute;
    width: 6px;
    height: 6px;
    background-color: #029ec0;
    border-radius: 50%;
  }
}
</style>